  
 
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Col, Dropdown, Row, DropdownButton as DropdownButtonRaw } from 'react-bootstrap';

import {
  DndContext,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Spinner from 'react-bootstrap/Spinner';

import { useCurrentUser, useModal, usePermission } from 'hooks';
import api from 'services/api';

import { useSnackbar } from 'v3/components/Snackbar';
import { Text, DropdownButton, Button } from 'v3/components';

import ApplicationLayout from 'v3/layouts/Application';

import { validatePermission } from 'actions';
import IndicatorsModal from './Modals/Indicators';
import AddModal from './Modals/Add';
import EditModal from './Modals/Edit';
import * as Cards from './Cards';

import DashboardContext from './context';

import { DraggableDiv } from './styled';

const restrictedViewCards = ['CargonRevenue'];

const [DashboardProvider] = DashboardContext;
const DROPPABLE = { FIRST: 'first', LAST: 'last' };

function DroppableColumn({ id, children }) {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Col ref={setNodeRef} xs={6}>
      {children}
    </Col>
  );
}

function SortableCard({ id, draggingEnable, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !draggingEnable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <DraggableDiv draggingEnable={draggingEnable} isDragging={isDragging}>
        {children}
      </DraggableDiv>
    </div>
  );
}

function mergeColumns(columns) {
  const biggerLength = Math.max(columns.first.length, columns.last.length);
  const temp = new Array(biggerLength * 2).fill(0);
  let hold = 0;
  return temp.map((_, index) => {
    if (index % 2 === 0) {
      return columns.first[hold] || null;
    }

    return columns.last[hold++] || null;
  });
}

export default function Dashboards() {
  const indicatorsModal = useModal();
  const addModal = useModal();
  const editModal = useModal();
  const user = useCurrentUser();
  const snackbar = useSnackbar();
  const navigate = useNavigate();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draggingEnable, setDraggingEnable] = useState(false);
  const [selected, setSelected] = useState(null);
  const [components, setComponents] = useState([]);
  const hasPermission = usePermission('SUPER_ADM');
  const hasPermissionToSeeDashboards = validatePermission('VISUALIZAR_DASHBOARD');

  useEffect(() => {
    if (!hasPermissionToSeeDashboards) {
      snackbar.show(<Text>Você não tem permissão para acessar os dashboards!</Text>, {
        type: 'error',
      });
      setTimeout(() => {
        navigate('/');
      }, 500);
    }
  }, []);

  const componentsArray = useMemo(() => {
    const first = components
      ?.map((item, idx) => {
        if (idx % 2 === 0) return item;
        return null;
      })
      ?.filter((item) => !!item);

    const last = components
      ?.map((item, idx) => {
        if (idx % 2 !== 0) return item;
        return null;
      })
      ?.filter((item) => !!item);

    return { first, last };
  }, [components]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function findContainer(itemId) {
    const firstHas = componentsArray.first?.some((item) => String(item.card_id) === String(itemId));
    if (firstHas) return DROPPABLE.FIRST;
    const lastHas = componentsArray.last?.some((item) => String(item.card_id) === String(itemId));
    if (lastHas) return DROPPABLE.LAST;
    return null;
  }

  async function fetchDashboards() {
    try {
      setLoading(true);
      const response = await api.get(`v3/dashboard/${user.id}`);

      const { data } = response;
      setOptions(data);
      if (data.length) {
        if (!selected) {
          setSelected(data[0]);
        } else {
          setSelected(data.find((dash) => dash.title === selected.title));
        }
      }
    } catch (error) {
      // handle exception
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    try {
      setLoading(true);

      await api.delete(`v3/dashboard/${selected.id}`);

      snackbar.show(<Text>Dashboard excluída com sucesso!</Text>, {
        type: 'success',
      });
    } catch (ex) {
      snackbar.show(<Text>Não foi possível alterar os dados, tente novamente mais tarde!</Text>, {
        type: 'error',
      });
    } finally {
      window.location.reload();
      setLoading(false);
    }
  }

  async function refreshDashboard() {
    await fetchDashboards();

    const tempComponents = components;

    setComponents();

    setTimeout(() => {
      setComponents(tempComponents);
    }, 100);
  }

  useEffect(() => {
    if (selected) {
      setComponents(
        JSON.parse(selected?.parameters)?.items?.map((item) => ({
          name: item.component,
          card_id: item.card_id,
        })),
      );
    }
  }, [selected]);

  useEffect(() => {
    fetchDashboards();
  }, []);

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over) return;

    const sourceId = findContainer(active.id);
    const overContainer =
      findContainer(over.id) || (over.id === DROPPABLE.FIRST || over.id === DROPPABLE.LAST ? over.id : null);

    if (!sourceId || !overContainer) return;
    if (sourceId === overContainer && active.id === over.id) return;

    if (sourceId === overContainer) {
      const items = componentsArray[sourceId];
      const oldIndex = items.findIndex((item) => String(item.card_id) === String(active.id));
      const newIndex = items.findIndex((item) => String(item.card_id) === String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      const isFirst = sourceId === DROPPABLE.FIRST;
      const reordered = arrayMove(items, oldIndex, newIndex);
      const next = {};
      next[sourceId] = reordered;
      next[isFirst ? DROPPABLE.LAST : DROPPABLE.FIRST] = isFirst ? componentsArray.last : componentsArray.first;
      setComponents(mergeColumns(next));
      return;
    }

    const sourceItems = componentsArray[sourceId];
    const destItems = componentsArray[overContainer];
    const activeIndex = sourceItems.findIndex((item) => String(item.card_id) === String(active.id));
    if (activeIndex === -1) return;

    let overIndex = destItems.findIndex((item) => String(item.card_id) === String(over.id));
    if (over.id === overContainer) overIndex = destItems.length;
    if (overIndex === -1) overIndex = destItems.length;

    const sourceClone = Array.from(sourceItems);
    const destClone = Array.from(destItems);
    const [removed] = sourceClone.splice(activeIndex, 1);
    destClone.splice(overIndex, 0, removed);

    const next = {};
    next[sourceId] = sourceClone;
    next[overContainer] = destClone;
    setComponents(mergeColumns(next));
  }

  function handleReorder() {
    setDraggingEnable(true);
  }

  async function handleOrderSave() {
    try {
      await api.put(`v3/dashboard/${selected.id}/order`, { components });

      snackbar.show(<Text>Nova ordem de dashboard salva com sucesso!</Text>, {
        type: 'success',
      });
    } catch (ex) {
      snackbar.show(
        <Text>Não foi possível salvar a nova ordem da dashboard, tente novamente mais tarde.</Text>,
        { type: 'error' },
      );
    } finally {
      setLoading(false);
      setDraggingEnable(false);
    }
  }

  return (
    <ApplicationLayout
      title={
        <div>
          <Col md={6} xs={12} className="p-0 my-2">
            {loading ? (
              <Spinner animation="border" role="status" />
            ) : (
              <DropdownButtonRaw
                id="dropdown-basic-button"
                title={selected?.title || 'Minha Dashboard'}
              >
                {options?.map((dashboard) => (
                  <Dropdown.Item onClick={() => setSelected(dashboard)}>
                    <Text weight={500}>{dashboard?.title}</Text>
                  </Dropdown.Item>
                ))}
                {options.length > 0 && <Dropdown.Divider />}
                <Dropdown.Item className="text-center" onClick={() => addModal.open()}>
                  <Text weight={500}>Nova Dashboard</Text>
                </Dropdown.Item>
              </DropdownButtonRaw>
            )}
          </Col>
        </div>
      }
      RightComponent={
        <>
          {!!options.length && (
            <div className="d-flex justify-content-end">
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                {draggingEnable && (
                  <Button variant="primary" onClick={handleOrderSave} className="py-2">
                    <Text weight={500} color="white" type="regular">
                      Salvar ordem
                    </Text>
                  </Button>
                )}
                {!draggingEnable && (
                  <>
                    <Button variant="primary" onClick={refreshDashboard}>
                      <Text weight={500} color="white" type="regular">
                        Atualizar dados
                      </Text>
                    </Button>

                    <DropdownButton
                      title="Editar dashboard"
                      size="md"
                      variant="success"
                      options={[
                        {
                          title: '  Editar ordenação',
                          onClick: () => handleReorder(),
                          type: 'item',
                        },
                        {
                          title: ' Selecionar indicadores',
                          onClick: () => indicatorsModal.open(),
                          type: 'item',
                        },

                        {
                          title: '  Editar título',
                          onClick: () => editModal.open(),
                          type: 'item',
                        },

                        {
                          type: 'divider',
                        },
                        {
                          title: ' Remover dashboard',
                          onClick: () => handleDelete(),
                          type: 'item',
                        },
                      ]}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </>
      }
      contentFluid
    >
      <DashboardProvider value={{ fetchDashboards, draggingEnable }}>
        <AddModal
          isOpen={addModal.isOpen}
          onClose={addModal.close}
          fetchDashboards={fetchDashboards}
          dashboardCount={options.length + 1}
          setSelected={setSelected}
        />
        <EditModal
          isOpen={editModal.isOpen}
          onClose={editModal.close}
          fetchDashboards={fetchDashboards}
          dashboardSelected={selected}
          setSelected={setSelected}
          setOptions={setOptions}
        />
        <IndicatorsModal
          isOpen={indicatorsModal.isOpen}
          onClose={indicatorsModal.close}
          components={components}
          fetchDashboards={fetchDashboards}
          dashboardSelected={selected}
          setSelected={setSelected}
        />
        {draggingEnable ? (
          <Row>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={componentsArray.first?.map((item) => String(item.card_id)) || []}
                strategy={verticalListSortingStrategy}
              >
                <DroppableColumn id={DROPPABLE.FIRST}>
                  {componentsArray.first?.map((component) => {
                    const Component = Cards[component?.name];
                    if (!Component) return null;
                    return (
                      <SortableCard
                        key={component?.card_id}
                        id={String(component?.card_id)}
                        draggingEnable={draggingEnable}
                      >
                        <Component
                          dashboard={selected}
                          id={component?.card_id}
                          name={component?.name}
                        />
                      </SortableCard>
                    );
                  })}
                </DroppableColumn>
              </SortableContext>
              <SortableContext
                items={componentsArray.last?.map((item) => String(item.card_id)) || []}
                strategy={verticalListSortingStrategy}
              >
                <DroppableColumn id={DROPPABLE.LAST}>
                  {componentsArray.last?.map((component) => {
                    const Component = Cards[component?.name];
                    if (!Component) return null;
                    return (
                      <SortableCard
                        key={component?.card_id}
                        id={String(component?.card_id)}
                        draggingEnable={draggingEnable}
                      >
                        <Component
                          dashboard={selected}
                          id={component?.card_id}
                          name={component?.name}
                        />
                      </SortableCard>
                    );
                  })}
                </DroppableColumn>
              </SortableContext>
            </DndContext>
          </Row>
        ) : (
          <Row>
            {components?.map((component) => {
              const Component = Cards[component?.name];
              const hideComponent = restrictedViewCards.includes(component?.name) && !hasPermission;

              if (!hideComponent && Component)
                return (
                  <Component dashboard={selected} id={component?.card_id} name={component?.name} />
                );
              return null;
            })}
          </Row>
        )}
      </DashboardProvider>
    </ApplicationLayout>
  );
}
